import { format, getQuarter } from "date-fns";
import {
  ComponentOperationType,
  DatePickerRangeDTO,
  FeedFilterDataTypes,
  GroopitFilterComponentDisplayDTO,
} from "groopit-js/client";

export const getDateDescription = (filteredDateRange?: DatePickerRangeDTO) => {
  switch (filteredDateRange?.id) {
    case "LastDay":
      return "last 24 hours";
    case "CurrentWeek":
      return "current week";
    case "PreviousWeek":
      return "previous week";
    case "CurrentMonth":
    case "PreviousMonth":
      return `${format(filteredDateRange.startDate as Date, "LLLL")}`;
    case "CurrentQuarter":
    case "PreviousQuarter":
      return `Q${getQuarter(filteredDateRange.startDate as Date)}`;
    case "CurrentYear":
    case "PreviousYear":
      return `${format(filteredDateRange.startDate as Date, "yyyy")}`;
    case "Custom":
      if (filteredDateRange.startDate && filteredDateRange.endDate) {
        return `${(
          filteredDateRange.startDate as Date
        ).toLocaleDateString()} through ${(
          filteredDateRange.endDate as Date
        ).toLocaleDateString()}`;
      } else if (filteredDateRange.startDate) {
        return `since ${(
          filteredDateRange.startDate as Date
        ).toLocaleDateString()}`;
      } else if (filteredDateRange.endDate) {
        return `through ${(
          filteredDateRange.endDate as Date
        ).toLocaleDateString()}`;
      } else {
        // Start date and end date should really have been set when
        // using custom but there was a bug at one point where they
        // weren't so we'll handle it here to be safe.
        return "all dates";
      }
    default:
      return "all dates";
  }
};

export const getCreatorDescription = (
  filteredCreators: { id: string; friendlyName: string }[],
) => {
  if (filteredCreators.length == 0) {
    return "all contributors";
  } else if (filteredCreators.length == 1) {
    return filteredCreators[0].friendlyName;
  } else {
    return `${filteredCreators.length} contributors`;
  }
};

const getNumericOperationName = (operation: ComponentOperationType) => {
  switch (operation) {
    case ComponentOperationType.GreaterThan:
      return "> ";
    case ComponentOperationType.GreaterThanOrEqual:
      return ">= ";
    case ComponentOperationType.LessThan:
      return "<";
    case ComponentOperationType.LessThanOrEqual:
      return "<=";
    default:
      return "";
  }
};

export const getComponentFilterDescription = (
  filter: GroopitFilterComponentDisplayDTO,
) => {
  if (filter.operation === ComponentOperationType.None) {
    return `No "${filter.componentModelDescription}"`;
  } else if (filter.operation === ComponentOperationType.FollowUpComplete) {
    return `${filter.componentModelDescription}: marked complete`;
  } else if (filter.operation === ComponentOperationType.FollowUpIncomplete) {
    return `${filter.componentModelDescription}: not complete`;
  } else if (Number.isFinite(filter.value)) {
    return `${getNumericOperationName(filter.operation)}${filter.value}`;
  } else if (Array.isArray(filter.value)) {
    return filter.value.map((v) => `"${v}"`).join(", ");
  } else if (filter.value instanceof DatePickerRangeDTO) {
    return getDateDescription(filter.value as DatePickerRangeDTO);
  } else {
    return filter.value;
  }
};

const all = "All";

export const ReadableDataTypeNames = new Map();
ReadableDataTypeNames.set(all, all);
ReadableDataTypeNames.set(FeedFilterDataTypes.Picture, "Pictures");
ReadableDataTypeNames.set(FeedFilterDataTypes.Location, "Location");
ReadableDataTypeNames.set(FeedFilterDataTypes.Text, "Text");
ReadableDataTypeNames.set(FeedFilterDataTypes.Tags, "Tags");
ReadableDataTypeNames.set(FeedFilterDataTypes.DateTime, "Date / Time");
ReadableDataTypeNames.set(FeedFilterDataTypes.Lists, "Lists");
ReadableDataTypeNames.set(FeedFilterDataTypes.Numbers, "Numbers");
ReadableDataTypeNames.set(FeedFilterDataTypes.Pictures, "Pictures (multiple)");
ReadableDataTypeNames.set(FeedFilterDataTypes.Files, "Files");
ReadableDataTypeNames.set(
  FeedFilterDataTypes.SaveToSalesforce,
  "Save to Salesforce",
);
ReadableDataTypeNames.set(
  FeedFilterDataTypes.FollowUpMarkedComplete,
  "Follow-up marked complete",
);
ReadableDataTypeNames.set(
  FeedFilterDataTypes.FollowUpNotMarkedComplete,
  "Follow-up not marked complete",
);
ReadableDataTypeNames.set(FeedFilterDataTypes.Flags, "Flags");
ReadableDataTypeNames.set(FeedFilterDataTypes.Comments, "Comments");
